/**
 * Centralized mock/seed data provider for Emad Platform demos.
 *
 * IMPORTANT (per PRD Message 105 - Rule 3):
 *  - Do NOT alter existing backend API contracts.
 *  - Do NOT invent fake API endpoints.
 *  - This module powers operational UI states (Pending, Rejected, Overdue,
 *    Awaiting review, At-risk, etc.) in dashboards for demo purposes only.
 *  - Every entry is clearly labeled `demo: true` so consumers can badge it
 *    visually as demonstration data.
 *
 * Consumers import from here and merge with real API data:
 *   const rows = realData.length ? realData : mockData.<slice>;
 */

// ---------- Helpers ---------------------------------------------------------
const daysFromNow = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString();
};

const hoursFromNow = (n) => {
  const d = new Date();
  d.setHours(d.getHours() + n);
  return d.toISOString();
};

// ---------- Association (الجمعية) — Operational Workspace -------------------
export const associationMocks = {
  // Training hours awaiting review from the association
  pendingHours: [
    {
      id: "mh-1001",
      student_name: "منى الحربي",
      program: "أساسيات المحاسبة",
      hours: 6,
      submitted_at: hoursFromNow(-4),
      note: "تسليم أسبوعي — قاعة التدريب رقم 3",
      demo: true,
    },
    {
      id: "mh-1002",
      student_name: "خالد العتيبي",
      program: "تطوير الويب — Frontend",
      hours: 4,
      submitted_at: hoursFromNow(-22),
      note: "متابعة مع الموجّه سلمان",
      demo: true,
    },
    {
      id: "mh-1003",
      student_name: "لينا الشمري",
      program: "التسويق الرقمي",
      hours: 3,
      submitted_at: hoursFromNow(-30),
      note: "زيارة ميدانية",
      demo: true,
    },
  ],

  // Students who are behind on tasks or attendance
  overdueStudents: [
    {
      id: "st-2001",
      name: "بندر السبيعي",
      program: "أساسيات المحاسبة",
      last_active_days: 9,
      overdue_tasks: 2,
      demo: true,
    },
    {
      id: "st-2002",
      name: "سارة القحطاني",
      program: "التسويق الرقمي",
      last_active_days: 12,
      overdue_tasks: 3,
      demo: true,
    },
  ],

  // Incoming requests to join the association's cohort
  joinRequests: [
    {
      id: "jr-3001",
      name: "أحمد الغامدي",
      city: "الرياض",
      program_interest: "تطوير الويب — Frontend",
      submitted_at: hoursFromNow(-6),
      demo: true,
    },
    {
      id: "jr-3002",
      name: "نورة الدوسري",
      city: "جدة",
      program_interest: "التسويق الرقمي",
      submitted_at: hoursFromNow(-18),
      demo: true,
    },
  ],

  // Upcoming on-site visits / scheduled activities
  upcomingVisits: [
    {
      id: "vs-4001",
      title: "زيارة ميدانية — قسم المحاسبة",
      scheduled_at: daysFromNow(1),
      location: "مقر الجمعية — الدور الثاني",
      demo: true,
    },
    {
      id: "vs-4002",
      title: "ورشة عمل — أدوات التسويق الرقمي",
      scheduled_at: daysFromNow(3),
      location: "قاعة التدريب رقم 1",
      demo: true,
    },
  ],
};

// ---------- Mentor (الموجّه) — Operational Workspace ------------------------
export const mentorMocks = {
  // Prominent "next session" card
  nextSession: {
    id: "ms-5001",
    student_name: "منى الحربي",
    title: "متابعة الأسبوع الثالث — أساسيات المحاسبة",
    scheduled_at: hoursFromNow(20),
    duration_minutes: 45,
    agenda: "مراجعة تمارين القيود المحاسبية + خطة الأسبوع القادم.",
    demo: true,
  },

  // Deliverables awaiting mentor evaluation
  tasksAwaitingEvaluation: [
    {
      id: "tk-6001",
      student_name: "خالد العتيبي",
      title: "تصميم صفحة هبوط باستخدام Tailwind",
      submitted_at: hoursFromNow(-2),
      priority: "high",
      demo: true,
    },
    {
      id: "tk-6002",
      student_name: "لينا الشمري",
      title: "خطة محتوى شهرية على LinkedIn",
      submitted_at: hoursFromNow(-8),
      priority: "medium",
      demo: true,
    },
    {
      id: "tk-6003",
      student_name: "منى الحربي",
      title: "حل تمارين القيود المحاسبية — الأسبوع 3",
      submitted_at: hoursFromNow(-26),
      priority: "high",
      demo: true,
    },
  ],

  // Students who need attention (slow progress / missed sessions)
  atRiskStudents: [
    {
      id: "ar-7001",
      name: "بندر السبيعي",
      program: "أساسيات المحاسبة",
      progress: 32,
      missed_sessions: 2,
      demo: true,
    },
    {
      id: "ar-7002",
      name: "سارة القحطاني",
      program: "التسويق الرقمي",
      progress: 41,
      missed_sessions: 1,
      demo: true,
    },
  ],

  // Pending post-session evaluations to be written
  pendingAssessments: [
    {
      id: "pa-8001",
      student_name: "أحمد الغامدي",
      session_title: "جلسة أسبوعية — Frontend",
      completed_at: hoursFromNow(-30),
      demo: true,
    },
    {
      id: "pa-8002",
      student_name: "نورة الدوسري",
      session_title: "جلسة أسبوعية — التسويق الرقمي",
      completed_at: hoursFromNow(-54),
      demo: true,
    },
  ],
};

// ---------- Super Admin (مدير النظام) — Operational Workspace ---------------
export const adminMocks = {
  pendingApprovals: [
    { id: "ap-9001", type: "application", label: "طلب تقديم", subject: "أحمد الغامدي — التسويق الرقمي", submitted_at: hoursFromNow(-3), demo: true },
    { id: "ap-9002", type: "opportunity", label: "فرصة جديدة", subject: "دبلوم إدارة المشاريع (مسودة)", submitted_at: hoursFromNow(-9), demo: true },
    { id: "ap-9003", type: "association", label: "طلب انضمام جهة", subject: "جمعية بناء", submitted_at: hoursFromNow(-30), demo: true },
    { id: "ap-9004", type: "certificate", label: "شهادة للاعتماد", subject: "لينا الشمري — أساسيات المحاسبة", submitted_at: hoursFromNow(-50), demo: true },
  ],
  systemAlerts: [
    { id: "sa-1", level: "warning", text: "خمسة طلاب تجاوزوا 10 أيام دون نشاط", demo: true },
    { id: "sa-2", level: "info", text: "3 فرص مسودّة بحاجة لمراجعة قبل النشر", demo: true },
    { id: "sa-3", level: "success", text: "12 شهادة أُصدرت هذا الأسبوع", demo: true },
  ],
  weeklyKPIs: {
    new_signups: 34,
    completed_courses: 12,
    issued_certificates: 12,
    active_associations: 8,
    demo: true,
  },
  topAssociations: [
    { id: "ta-1", name: "جمعية النور الخيرية", students: 24, completion_rate: 78, demo: true },
    { id: "ta-2", name: "جمعية بنيان", students: 18, completion_rate: 71, demo: true },
    { id: "ta-3", name: "مؤسسة عطاء", students: 12, completion_rate: 66, demo: true },
  ],
};

// ---------- Donor (المؤسسة المانحة) — Impact Workspace ----------------------
export const donorMocks = {
  fundedPrograms: [
    { id: "fp-1", name: "التسويق الرقمي", students: 45, graduated: 28, budget_used_pct: 62, demo: true },
    { id: "fp-2", name: "أساسيات المحاسبة", students: 32, graduated: 21, budget_used_pct: 71, demo: true },
    { id: "fp-3", name: "تطوير الويب — Frontend", students: 24, graduated: 9, budget_used_pct: 44, demo: true },
  ],
  impactStats: {
    total_beneficiaries: 101,
    graduated: 58,
    hours_delivered: 4820,
    associations_reached: 6,
    demo: true,
  },
  monthlyReports: [
    { id: "mr-1", title: "تقرير مارس 2026", published_at: daysFromNow(-2), format: "PDF", demo: true },
    { id: "mr-2", title: "تقرير فبراير 2026", published_at: daysFromNow(-32), format: "PDF", demo: true },
    { id: "mr-3", title: "تقرير يناير 2026", published_at: daysFromNow(-61), format: "PDF", demo: true },
  ],
  storyHighlights: [
    { id: "sh-1", name: "سارة العتيبي", program: "التسويق الرقمي", quote: "التجربة في عماد فتحت لي أبواب سوق العمل.", demo: true },
    { id: "sh-2", name: "عبدالله الشمري", program: "تقنية المعلومات", quote: "الجلسات الأسبوعية غيّرت مساري تماماً.", demo: true },
  ],
};

// ---------- Content Creator (معدّ المحتوى) — Studio Workspace ---------------
export const contentMocks = {
  draftCourses: [
    { id: "dc-1", title: "أساسيات إدارة المشاريع", sections: 6, materials: 22, quizzes: 3, updated_at: hoursFromNow(-8), demo: true },
    { id: "dc-2", title: "مقدمة في التسويق بالمحتوى", sections: 4, materials: 14, quizzes: 2, updated_at: hoursFromNow(-40), demo: true },
  ],
  quizPerformance: [
    { id: "qp-1", course: "أساسيات المحاسبة", quiz: "الفصل الأول", avg_score: 82, attempts: 45, demo: true },
    { id: "qp-2", course: "التسويق الرقمي", quiz: "الأساسيات", avg_score: 74, attempts: 38, demo: true },
    { id: "qp-3", course: "Frontend", quiz: "HTML & CSS", avg_score: 68, attempts: 22, demo: true },
  ],
  learnerFeedback: [
    { id: "lf-1", learner: "منى الحربي", course: "أساسيات المحاسبة", rating: 5, comment: "شرح ممتاز وأمثلة عملية." , demo: true },
    { id: "lf-2", learner: "خالد العتيبي", course: "Frontend", rating: 4, comment: "المادة رائعة، أتمنى المزيد من التمارين.", demo: true },
    { id: "lf-3", learner: "لينا الشمري", course: "التسويق الرقمي", rating: 5, comment: "أحببت الاختبارات القصيرة بين الدروس.", demo: true },
  ],
  studioTasks: [
    { id: "st-1", task: "تسجيل 3 فيديوهات جديدة", due_at: daysFromNow(2), demo: true },
    { id: "st-2", task: "مراجعة اختبار الفصل الثاني", due_at: daysFromNow(4), demo: true },
    { id: "st-3", task: "رفع مواد المرفقات (PDF)", due_at: daysFromNow(7), demo: true },
  ],
};

// ---------- Utility: relative time in Arabic ---------------------------------
export function timeAgoAr(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "قبل لحظات";
  if (diff < 3600) return `قبل ${Math.floor(diff / 60)} دقيقة`;
  if (diff < 86400) return `قبل ${Math.floor(diff / 3600)} ساعة`;
  const days = Math.floor(diff / 86400);
  if (days < 7) return `قبل ${days} يوم`;
  if (days < 30) return `قبل ${Math.floor(days / 7)} أسبوع`;
  return `قبل ${Math.floor(days / 30)} شهر`;
}

export function timeFromNowAr(iso) {
  if (!iso) return "";
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  if (diff < 0) return timeAgoAr(iso);
  if (diff < 3600) return `خلال ${Math.max(1, Math.floor(diff / 60))} دقيقة`;
  if (diff < 86400) return `خلال ${Math.floor(diff / 3600)} ساعة`;
  const days = Math.floor(diff / 86400);
  return `خلال ${days} يوم`;
}
